import L from "leaflet";
import type { ForteXApi, KeyValue, TileFeature, TileField } from "@fortex/api";
import type { CatalogueEntry } from "../catalogue/types";
import type { InfoFeature, InfoHit, InfoProvider, LayerSource, SourceContext, SourceFactory } from "./types";

export class AuthenticatedTileLayer extends L.TileLayer {
  private readonly api: ForteXApi;

  constructor(api: ForteXApi, url: string, options: L.TileLayerOptions) {
    super(url, options);
    this.api = api;
    this.on("tileunload", event => {
      const tile = (event as L.TileEvent).tile as HTMLImageElement;

      if (tile.src.startsWith("blob:")) URL.revokeObjectURL(tile.src);
    });
  }

  override createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const tile = document.createElement("img");

    tile.alt = "";
    tile.setAttribute("role", "presentation");
    tile.addEventListener("load", () => done(undefined, tile));
    tile.addEventListener("error", () => done(new Error("tile"), tile));

    this.api
      .blob(this.getTileUrl(coords))
      .then(blob => {
        if (blob === null) {
          done(new Error("tile"), tile);
          return;
        }

        tile.src = URL.createObjectURL(blob);
      })
      .catch(error => done(error instanceof Error ? error : new Error("tile"), tile));

    return tile;
  }
}

export function entryBounds(entry: CatalogueEntry): L.LatLngBounds | null {
  const bounds = entry.bounds ?? entry.source?.bounds ?? null;

  if (bounds === null) return null;

  const result = L.latLngBounds([bounds.minLat, bounds.minLon], [bounds.maxLat, bounds.maxLon]);

  return result.isValid() ? result : null;
}

export function fieldsFor(feature: TileFeature, catalogue: Record<string, TileField[]>, fetched: { key: string; value: TileField[] }[] | null, layer: string): TileField[] {
  if (feature.fields && feature.fields.length > 0) return feature.fields;

  const own = catalogue[feature.layer ?? layer] ?? catalogue[layer];

  if (own && own.length > 0) return own;

  const match = fetched?.find(a => a.key.toLowerCase() === (feature.layer ?? layer).toLowerCase()) ?? fetched?.[0];

  return match?.value ?? [];
}

export function createTileInfoProvider(api: ForteXApi, entry: CatalogueEntry, context: SourceContext): InfoProvider {
  const layerName = entry.idName;
  let fieldCache: { key: string; value: TileField[] }[] | null = null;

  const withFields = async (feature: TileFeature, signal: AbortSignal): Promise<TileFeature> => {
    if (feature.fields && feature.fields.length > 0) return feature;

    const catalogueFields = entry.source?.fields ?? {};

    if (Object.keys(catalogueFields).length === 0 && fieldCache === null) {
      try {
        fieldCache = await api.fields([layerName], signal);
      } catch {
        fieldCache = [];
      }
    }

    return { ...feature, fields: fieldsFor(feature, catalogueFields, fieldCache, layerName) };
  };

  return {
    hitTest: async (latlng, zoom, signal) => {
      const hits = await api.click(latlng[0], latlng[1], zoom, [layerName], signal);

      return hits.map(a => ({ id: a.id, layer: layerName, name: a.name, feature: null }) as InfoHit);
    },
    feature: async (hit, latlng, zoom, signal) => {
      const nearby = await api.featureAt(hit.id, latlng[0], latlng[1], zoom, [layerName], signal).catch(() => null);
      const feature = nearby ?? (await api.feature(hit.id, layerName, signal));

      if (feature === null) throw new Error(`${layerName}/${hit.id}`);

      const complete = await withFields(feature, signal);

      return { hit, feature: complete, editable: context.admin() && complete.id !== null } as InfoFeature;
    },
    save: async (hit, data: KeyValue[]) => {
      const updated = await api.updateFeature(layerName, hit.id, data);
      const complete = await withFields(updated, new AbortController().signal);

      return { hit, feature: complete, editable: context.admin() };
    }
  };
}

export function createTilesSource(api: ForteXApi): SourceFactory {
  return (entry, context) => {
    const tiles = entry.tiles;

    if (tiles === null) return null;

    const pane = context.paneFor(entry);
    const options: L.TileLayerOptions = {
      minZoom: tiles.minZoom,
      maxZoom: tiles.maxZoom,
      noWrap: true,
      zIndex: context.zIndexFor(entry),
      pane,
      attribution: entry.attribution ?? ""
    };
    const layer = context.authenticated() ? new AuthenticatedTileLayer(api, tiles.tileUrlTemplate, options) : L.tileLayer(tiles.tileUrlTemplate, options);
    let current = entry;

    const source: LayerSource = {
      entry,
      kind: entry.kind,
      layer,
      pane,
      bounds: () => entryBounds(current),
      features: null,
      info: createTileInfoProvider(api, entry, context),
      legend: () => current.legend,
      setOpacity: value => layer.setOpacity(value),
      update: next => {
        if (next.tiles !== null && next.tiles.tileUrlTemplate !== current.tiles?.tileUrlTemplate) layer.setUrl(next.tiles.tileUrlTemplate);

        current = next;
        source.entry = next;
      },
      dispose: () => layer.remove()
    };

    return source;
  };
}
